/**
 * Global Fetch Interceptor for Club Authentication
 * 
 * This module overrides the global fetch function to automatically
 * add the X-Club-Key header to all API requests.
 * 
 * Import this ONCE at the app entry point (index.js) BEFORE any other imports.
 */

const CLUB_KEY_STORAGE = 'masaklubi_club_key';
const USER_STORAGE = 'masaklubi_user';

// Store original fetch
const originalFetch = window.fetch;

// Public endpoints that don't need authentication
const PUBLIC_ENDPOINTS = [
    '/api/login',
    '/api/onboarding/start',
    '/api/onboarding/verify',
    '/api/onboarding/complete',
    '/api/bgg/search',
    '/api/bgg/hot',
    '/api/season-config'
];

/**
 * Check if URL is a public endpoint
 */
function isPublicEndpoint(url) {
    const urlPath = typeof url === 'string' ? url : url.pathname || '';
    return PUBLIC_ENDPOINTS.some(ep => urlPath.startsWith(ep));
}

/**
 * Get club key from storage - tries multiple sources
 */
function getClubKey() {
    // First try the dedicated key storage
    let clubKey = localStorage.getItem(CLUB_KEY_STORAGE);
    
    // If not found, try to extract from stored user object
    if (!clubKey) {
        try {
            const userJson = localStorage.getItem(USER_STORAGE);
            if (userJson) {
                const user = JSON.parse(userJson);
                clubKey = user?.club?.apiKey;
                
                // If found in user, store it separately for future use
                if (clubKey) {
                    localStorage.setItem(CLUB_KEY_STORAGE, clubKey);
                    console.log('[API] Club key migrated from user storage');
                }
            }
        } catch (e) {
            console.warn('[API] Failed to parse user storage:', e);
        }
    }
    
    return clubKey;
}

/**
 * Enhanced fetch that automatically adds X-Club-Key header
 */
window.fetch = function(url, options = {}) {
    // Only intercept API calls
    const urlString = typeof url === 'string' ? url : url.toString();
    
    if (urlString.startsWith('/api') && !isPublicEndpoint(urlString)) {
        const clubKey = getClubKey();
        
        if (clubKey) {
            // Clone options to avoid mutating original
            const newOptions = { ...options };
            
            // Merge headers
            newOptions.headers = {
                ...newOptions.headers,
                'X-Club-Key': clubKey
            };
            
            return originalFetch(url, newOptions);
        } else {
            console.warn(`[API] No club key found for ${urlString} - request may fail`);
        }
    }
    
    // Call original fetch for non-API calls or public endpoints
    return originalFetch(url, options);
};

// Export for testing
export const getOriginalFetch = () => originalFetch;

console.log('[API] Global fetch interceptor installed - X-Club-Key will be added to all /api calls');

